import React, { ReactNode, useEffect, useState } from "react";
import { LoginState } from "./LoginState";
import { useIdentityDao } from "./dao/useIdentityDao";
import { UserContext } from "shared-ts-utils-authentication";
import { IdentityUser, IdentityUserDto } from "./IdentityUser";
import {
  FetchError,
  UnauthenticatedAwareServerErrorMiddleware,
} from "shared-ts-utils-fetch";
import { IllegalStateError } from "shared-ts-utils-core";
import { MobileBootstrapSession } from "./MobileBootstrapSession";
import { MobileAuthBridge } from "./MobileAuthBridge";
import { ExternalMobileAuthBrowserNotice } from "./ExternalMobileAuthBrowserNotice";
import {
  ExternalMobileAuth,
  usePromoteCookieFlagToLocalStorageEffect,
} from "./ExternalMobileAuth";
import { useJustBootstrappedMobileAuthentication } from "./useJustBootstrappedMobileAuthentication";
import { LoginComponentProps } from "./LoginComponentProps";
import { LoginSessionExpiredAwareMessageNotifier } from "./LoginSessionExpiredAwareMessageNotifier";
import { LoginSessionExpired } from "./LoginSessionExpired";

const loginPath = "/login";

export interface LoginProviderProps<U extends IdentityUserDto> {
  loginComponent: React.ElementType<LoginComponentProps>;
  privacyPolicy?: {
    path: string;
    component: React.ElementType;
  };
  registerUser?: {
    path: string;
    component: React.ElementType;
  };
  acceptUserInvitation?: {
    path: string;
    component: React.ElementType;
  };
  confirmUser?: {
    path: string;
    component: React.ElementType;
  };
  resetPassword?: {
    path: string;
    component: React.ElementType;
  };
  authenticationCheck: () => Promise<boolean>;
  userMapper?: (user: U) => IdentityUser;
  children?: ReactNode | ((user: IdentityUser) => ReactNode);
}

export const LoginProvider = <U extends IdentityUserDto>(
  props: LoginProviderProps<U>
) => {
  const {
    loginComponent,
    privacyPolicy,
    registerUser,
    acceptUserInvitation,
    confirmUser,
    resetPassword,
    authenticationCheck,
    userMapper,
  } = props;
  const identityDao = useIdentityDao();
  const [accessChecked, setAccessChecked] = useState<boolean>(false);
  const [accessAuthorized, setAccessAuthorized] = useState<boolean>(false);
  const [loginState, setLoginState] = useState<LoginState>("Unauthorized");
  const [user, setUser] = useState<IdentityUser | undefined>(undefined);
  const isExternalMobileAuthorization =
    ExternalMobileAuth.hasUrlOrLocalStorageFlag();
  const justBootstrapped = useJustBootstrappedMobileAuthentication();

  const componentName = resolveLoginProviderComponentName(props);
  // Route '/login' is the generic login route
  const isGenericLoginRoute = componentName == null;

  useEffect(
    function checkAccess() {
      async function checkAccess() {
        try {
          const authenticated = await authenticationCheck();
          setAccessChecked(true);
          setAccessAuthorized(authenticated);
        } catch (e) {
          console.error(
            "LoginProvider: error while checking authentication: ",
            e
          );
          setAccessChecked(true);
        }
      }

      if (
        componentName == null ||
        ![
          loginProviderComponentNames.registerUser,
          loginProviderComponentNames.confirmUser,
          loginProviderComponentNames.mobileBootstrapSession,
        ].includes(componentName)
      ) {
        checkAccess();
      }
    },
    [authenticationCheck, componentName]
  );

  useEffect(
    function whenAuthorized() {
      async function whenAuthorized() {
        try {
          const userDto = await identityDao.getUser<U>();
          const user =
            userMapper != null
              ? userMapper(userDto)
              : new IdentityUser(userDto);
          setUser(user);
        } catch (e) {
          console.error("LoginProvider: Failed to get logged in user", e);
          if (e instanceof FetchError && e.response?.status === 401) {
            setAccessAuthorized(false);
          }
        }
      }

      if (accessAuthorized) {
        whenAuthorized();
      }
    },
    [accessAuthorized, userMapper, identityDao]
  );

  useEffect(function handleSessionExpiredEvent() {
    const handleSessionExpired = () => {
      console.warn(
        "LoginProvider: Session expired event received. Forcing logout."
      );

      LoginSessionExpired.raiseFlag();

      setAccessChecked(true);
      setAccessAuthorized(false);
      setLoginState("Unauthorized");
      setUser(undefined);
    };

    window.addEventListener(
      UnauthenticatedAwareServerErrorMiddleware.UnauthenticatedEventType,
      handleSessionExpired
    );
    return () => {
      window.removeEventListener(
        UnauthenticatedAwareServerErrorMiddleware.UnauthenticatedEventType,
        handleSessionExpired
      );
    };
  }, []);

  usePromoteCookieFlagToLocalStorageEffect();

  useEffect(() => {
    // Prevent false positives: if we're not in an active provider-level external flow
    // (no cookie/URL flag), and we're not on register or bootstrap routes, clear any
    // stale localStorage sticky so Safari direct logins won't be misclassified.
    try {
      if (
        !ExternalMobileAuth.isProviderExternalFlowActive() &&
        componentName !== loginProviderComponentNames.registerUser &&
        componentName !== loginProviderComponentNames.mobileBootstrapSession
      ) {
        if (ExternalMobileAuth.hasLocalStorageFlag()) {
          ExternalMobileAuth.removeLocalStorageFlag();
          // Best-effort: also ask server to clear cookie fallback (in case it lingered)
          ExternalMobileAuth.clearCookie();
        }
      }
    } catch {
      // noop
    }
    // We intentionally run this once on mount; dependencies on registerUser.path are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldRedirectToLogin =
    isGenericLoginRoute &&
    !justBootstrapped &&
    (!accessChecked ||
      (accessChecked && !accessAuthorized) ||
      (accessChecked && accessAuthorized && user === undefined));

  useEffect(() => {
    if (!shouldRedirectToLogin) {
      return;
    }

    // Remember the original path so we can restore it after successful login
    try {
      if (typeof window !== "undefined") {
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const isSafeRelative =
          current.startsWith("/") && !current.startsWith("//");
        if (
          !isExternalMobileAuthorization &&
          isSafeRelative &&
          !current.startsWith(loginPath)
        ) {
          sessionStorage.setItem("postLoginRedirect", current);
        }
      }
    } catch {
      // noop
    }

    ensurePath(loginPath);
  }, [shouldRedirectToLogin, isExternalMobileAuthorization]);

  const handleLogin = (result: LoginState) => {
    if (result === "Success") {
      setAccessAuthorized(true);
    } else {
      setAccessAuthorized(false);
    }
    setLoginState(result);
  };

  const handleRegisterNewUser = () => {
    if (registerUser != null) {
      try {
        if (typeof window !== "undefined") {
          // Consolidated logic using ExternalMobileAuth helper.
          // Goal: keep a single source of truth and minimal flags.
          // 1) If the cookie fallback exists but sticky not yet set, promote it.
          if (
            ExternalMobileAuth.hasCookieFlag() &&
            !ExternalMobileAuth.hasLocalStorageFlag()
          ) {
            ExternalMobileAuth.setLocalStorageFlag();
          }

          // 2) Ensure sticky is set when we already are in external mobile auth context
          const isExternal = ExternalMobileAuth.hasUrlOrLocalStorageFlag();
          if (isExternal && !ExternalMobileAuth.hasLocalStorageFlag()) {
            ExternalMobileAuth.setLocalStorageFlag();
          }

          // 3) Navigate to /register-user without appending URL flags.
          //    The sticky in localStorage is the canonical signal for downstream logic.
          window.location.href = registerUser.path;
          return;
        }
      } catch (e) {
        // If something goes wrong while preserving flags, fall back to the
        // original behaviour so we don't break desktop/web flows.
        console.error(
          "[LoginProvider] Failed to preserve mobile flags when navigating to register-user:",
          e
        );
      }

      window.location.href = registerUser.path;
    }
  };

  if (componentName === loginProviderComponentNames.confirmUser) {
    const ConfirmUserComponent = confirmUser?.component;
    if (ConfirmUserComponent == null) {
      throw new IllegalStateError("No ConfirmUserComponent configured");
    }
    return <ConfirmUserComponent />;
  } else if (componentName === loginProviderComponentNames.resetPassword) {
    const ResetPasswordComponent = resetPassword?.component;
    if (ResetPasswordComponent == null) {
      throw new IllegalStateError("No ResetPasswordComponent configured");
    }
    return <ResetPasswordComponent />;
  } else if (componentName === loginProviderComponentNames.privacyPolicy) {
    const PrivacyPolicyComponent = privacyPolicy?.component;
    if (PrivacyPolicyComponent == null) {
      throw new IllegalStateError("No PrivacyPolicyComponent configured");
    }
    return <PrivacyPolicyComponent />;
  } else if (componentName === loginProviderComponentNames.registerUser) {
    const RegisterUserComponent = registerUser?.component;
    if (RegisterUserComponent == null) {
      throw new IllegalStateError("No RegisterUserComponent configured");
    }
    return <RegisterUserComponent />;
  } else if (
    componentName === loginProviderComponentNames.acceptUserInvitation
  ) {
    const AcceptUserInvitationComponent = acceptUserInvitation?.component;
    if (AcceptUserInvitationComponent == null) {
      throw new IllegalStateError(
        "No AcceptUserInvitationComponent configured"
      );
    }
    return <AcceptUserInvitationComponent />;
  } else if (
    componentName === loginProviderComponentNames.mobileBootstrapSession
  ) {
    return <MobileBootstrapSession />;
  } else if (componentName === loginProviderComponentNames.mobileAuthBridge) {
    return <MobileAuthBridge />;
  }

  const LoginComponent = loginComponent;

  if (!accessChecked) {
    return (
      <LoginComponent
        onLoginStateChange={handleLogin}
        onRegisterNewUser={handleRegisterNewUser}
        loginState={"CheckingAccess"}
      />
    );
  } else if (accessChecked && !accessAuthorized) {
    return (
      <LoginSessionExpiredAwareMessageNotifier>
        <LoginComponent
          onLoginStateChange={handleLogin}
          onRegisterNewUser={handleRegisterNewUser}
          loginState={loginState}
        />
      </LoginSessionExpiredAwareMessageNotifier>
    );
  } else if (accessAuthorized && user == null) {
    // Stay on the current path to avoid re-entering /login in mobile WebView
    return (
      <LoginComponent
        onLoginStateChange={handleLogin}
        onRegisterNewUser={handleRegisterNewUser}
        loginState={"LoadingUser"}
      />
    );
  } else if (accessChecked && accessAuthorized && user !== undefined) {
    if (!justBootstrapped) {
      // If we're on the login page after successful login, restore the original path if available
      try {
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (current === loginPath || current === `${loginPath}/`) {
          const redirect = isExternalMobileAuthorization
            ? null
            : sessionStorage.getItem("postLoginRedirect");
          if (!isExternalMobileAuthorization) {
            sessionStorage.removeItem("postLoginRedirect");
          }
          const target =
            redirect && redirect.startsWith("/") && !redirect.startsWith("//")
              ? redirect
              : "/";
          // In external mobile browser mode we keep the user on /login so that we can
          // display an informational message about closing the browser, instead of
          // navigating into the app.
          if (!isExternalMobileAuthorization) {
            window.history.replaceState(null, "", target);
          }
        }
      } catch (_) {
        // noop
      }
    }
    if (isExternalMobileAuthorization) {
      // Show information instead of the app when we detect external mobile browser
      return <ExternalMobileAuthBrowserNotice />;
    }
    return (
      <UserContext.Provider value={user}>
        {typeof props.children === "function"
          ? props.children(user)
          : props.children}
      </UserContext.Provider>
    );
  } else {
    return (
      <LoginComponent
        onLoginStateChange={handleLogin}
        onRegisterNewUser={handleRegisterNewUser}
        loginState={loginState}
      />
    );
  }
};

const loginProviderComponentNames = {
  privacyPolicy: "PrivacyPolicy",
  registerUser: "RegisterUser",
  acceptUserInvitation: "AcceptUserInvitation",
  confirmUser: "ConfirmUser",
  resetPassword: "ResetPassword",
  mobileBootstrapSession: "MobileBootstrapSession",
  mobileAuthBridge: "MobileAuthBridge",
};

export type LoginProviderComponentName =
  (typeof loginProviderComponentNames)[keyof typeof loginProviderComponentNames];

const resolveLoginProviderComponentName = <U extends IdentityUserDto>({
  privacyPolicy,
  registerUser,
  acceptUserInvitation,
  confirmUser,
  resetPassword,
}: LoginProviderProps<U>): LoginProviderComponentName | null => {
  const windowPathName = window.location.pathname;

  const doRenderPrivacyPolicyRoute =
    privacyPolicy != null
      ? windowPathName.startsWith(privacyPolicy?.path)
      : false;
  if (doRenderPrivacyPolicyRoute)
    return loginProviderComponentNames.privacyPolicy;

  const doRenderRegisterUser =
    registerUser != null
      ? windowPathName.startsWith(registerUser?.path)
      : false;
  if (doRenderRegisterUser) return loginProviderComponentNames.registerUser;

  const doRenderAcceptUserInvitation =
    acceptUserInvitation != null
      ? windowPathName.startsWith(acceptUserInvitation?.path)
      : false;
  if (doRenderAcceptUserInvitation)
    return loginProviderComponentNames.acceptUserInvitation;

  const doRenderConfirmUser =
    confirmUser != null ? windowPathName.startsWith(confirmUser?.path) : false;
  if (doRenderConfirmUser) return loginProviderComponentNames.confirmUser;

  const doRenderResetPassword =
    resetPassword != null
      ? windowPathName.startsWith(resetPassword?.path)
      : false;
  if (doRenderResetPassword) return loginProviderComponentNames.resetPassword;
  const doRenderMobileBootstrapSession =
    windowPathName === "/mobile/bootstrap-session";
  if (doRenderMobileBootstrapSession)
    return loginProviderComponentNames.mobileBootstrapSession;

  const doRenderMobileAuthBridge = windowPathName === "/mobile/auth-bridge";
  if (doRenderMobileAuthBridge)
    return loginProviderComponentNames.mobileAuthBridge;
  return null;
};

const ensurePath = (desiredPath: string) => {
  try {
    if (typeof window !== "undefined") {
      const { pathname, search, hash } = window.location;

      // If navigating to /login, preserve the entire existing query string and hash (future-proof)
      // so that any flags relevant to mobile auth or error display are retained.
      let targetPath = desiredPath;
      if (desiredPath === loginPath) {
        // Preserve both the current query string and the hash fragment (anchors)
        targetPath = `${desiredPath}${search ?? ""}${hash ?? ""}`;
      }

      const currentPathQueryHash = `${pathname}${search}${hash}`;
      if (currentPathQueryHash !== targetPath) {
        window.history.replaceState(null, "", targetPath);
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // noop: if history API isn't available, don't crash rendering
  }
};
